import { useState } from "react";
import { Button } from "flowbite-react";
import { HiPencil, HiTrash } from "react-icons/hi";
import { EventType } from "@/sharedTypes";
import DataTable from "@/components/Table";
import { useUpdateEventTypeMutation, useDeleteEventTypeMutation } from "@/api/locationApi/eventType";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import EventTypeFormDialog from "./EventTypeFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";
import { useTranslation } from "react-i18next";

interface EventTypeListTableProps {
  data: EventType[];
  pagination: any;
  onPageChange: (page: number) => void;
}

export default function EventTypeListTable({ data, pagination, onPageChange }: EventTypeListTableProps) {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<EventType | undefined>();

  const [updateEventType] = useUpdateEventTypeMutation();
  const [deleteEventType] = useDeleteEventTypeMutation();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);

  const handleEdit = (eventType: EventType) => {
    setSelectedEventType(eventType);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (data: EventType) => {
    if (!selectedEventType) return;
    try {
      await updateEventType({ ...data, id: selectedEventType.id } as EventType).unwrap();
      toast.success(t("temporalActivity:eventType.updatedSuccess"));
      setIsEditModalOpen(false);
      setSelectedEventType(undefined);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = (eventType: EventType) => {
    setConfirmDialog({
      open: true,
      title: t("temporalActivity:eventType.confirmDeleteTitle"),
      description: t("temporalActivity:eventType.confirmDeleteBody"),
      onConfirm: async () => {
        try {
          await deleteEventType(eventType.id).unwrap();
          toast.success(t("temporalActivity:eventType.deletedSuccess"));
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
        setConfirmDialog(null);
      },
    });
  };

  const columns = [
    { key: "id", label: t("temporalActivity:eventType.ref") },
    { key: "event_type", label: t("temporalActivity:eventType.name") },
    { key: "description", label: t("temporalActivity:eventType.description") },
    ...(isCompanyAdminUser(user)
      ? [
          {
            key: "actions",
            label: t("temporalActivity:actions"),
            render: (row: EventType) => (
              <div className="flex gap-2">
                <Button size="xs" color="light" onClick={() => handleEdit(row)}>
                  <HiPencil className="h-4 w-4 text-purple-600" />
                </Button>
                <Button size="xs" color="light" onClick={() => handleDelete(row)}>
                  <HiTrash className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <DataTable columns={columns} data={data} pagination={pagination} onPageChange={onPageChange} />

      <EventTypeFormDialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        defaultValues={selectedEventType}
        mode="edit"
      />

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.description}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
        />
      )}
    </>
  );
}
